import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import { Divider, Stack, TextField, Autocomplete, Chip } from '@mui/material';
// components
import TextMaxLine from '../../../components/TextMaxLine';
import { FormProvider, RHFTextField } from '../../../components/hook-form';
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import { Category } from 'src/@types/Category';
import { UploadAvatar, UploadMultiFile, UploadSingleFile } from 'src/components/upload';
// ----------------------------------------------------------------------

type Props = {
  isEdit: boolean;
  currentCategory?: Category;
  handleClose(): void;
};

export default function CategoryNewEditForm({ isEdit, currentCategory, handleClose }: Props) {
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();
  const NewCategorySchema = Yup.object().shape({
    title: Yup.string()
      .min(3, 'path title must be greater than 3 character')
      .max(128, 'path title must be less than 128 character')
      .required('Path title is required'),
    icon: Yup.string().required('Path Icon is required'),
    image: Yup.string().required('Path Image is required'),
    active: Yup.boolean(),
    description: Yup.string().min(3).required('description is Required'),
    keywords: Yup.array().min(1).required('Keywords is Required'),
  });

  const defaultValues: Category = useMemo(
    () => ({
      title: currentCategory?.title || '',
      title_ar: currentCategory?.title_ar || '',
      image: currentCategory?.image || '',
      icon: currentCategory?.icon || '',
      description: currentCategory?.description || '',
      description_ar: currentCategory?.description_ar || '',
      active: currentCategory?.active || false,
      keywords: currentCategory?.keywords || [],
      keywords_ar: currentCategory?.keywords_ar || [],
    }),
    [currentCategory]
  );

  const methods = useForm<Category>({
    resolver: yupResolver(NewCategorySchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    handleSubmit,
    setValue,
    formState: { isSubmitting, errors },
  } = methods;

  const values = watch();

  useEffect(() => {
    if (isEdit && currentCategory) {
      reset(currentCategory);
    }
    if (!isEdit) {
      reset(currentCategory);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, currentCategory]);

  const onSubmit = async (data: Category) => {
    if (!data.title_ar) delete data?.title_ar;
    if (!data.description_ar) delete data?.description_ar;
    if (!data.keywords_ar) delete data?.keywords_ar;
    try {
      if (currentCategory)
        await axiosInstance.patch(APIS.CATEGORIES.PATCH(currentCategory._id as string), data);
      else await axiosInstance.post(APIS.CATEGORIES.ADD, data);
      reset();
      enqueueSnackbar(!isEdit ? 'Create success!' : 'Update success!');
      handleClose();
    } catch (err) {
      if (err?.message) {
        enqueueSnackbar(err?.message, { variant: 'error' });
      }

      if (err?.messages.length && err?.messages[0]) {
        enqueueSnackbar(err?.messages.length && err?.messages[0], { variant: 'error' });
      } else {
        enqueueSnackbar(!isEdit ? 'Unable to create Category!' : 'Unable to edit Category', {
          variant: 'error',
        });
      }
    }
  };

  const uploadImage = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    console.log('file', file);
    const response = await axiosInstance.post(APIS.UPLOAD.MEDIA, form);
    return response.data;
  };
  return (
    <Stack height={'100%'} style={{ overflow: 'scroll' }}>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <TextMaxLine marginBottom={'20px'} marginTop={'20px'} fontSize={'22px'}>
          {isEdit ? ' Edit Path' : ' Create Path'}
        </TextMaxLine>
        <Stack direction={'column'} justifyContent={'space-between'} height={'90%'}>
          <Stack direction={'column'} spacing={5}>
            <Stack direction={'row'} justifyContent={'center'} spacing={7}>
              <div>
                <UploadAvatar
                  accept={{ 'image/*': [] }}
                  error={errors.image ? true : false}
                  label="Path Image"
                  file={values.image}
                  onDropAccepted={async (file) => {
                    let response = await uploadImage(file[0]);
                    setValue('image', (response as any)?.url as string);
                  }}
                />
                <p style={{ color: '#FF4842', fontSize: '0.75rem', marginTop: '10px' }}>
                  {errors.image?.message}
                </p>
              </div>

              <Divider orientation="vertical" />
              <div>
                <UploadAvatar
                  accept={{ 'image/*': [] }}
                  label="Path Icon"
                  error={errors.icon ? true : false}
                  file={values.icon}
                  onDropAccepted={async (file) => {
                    let response = await uploadImage(file[0]);
                    setValue('icon', response?.url);
                  }}
                />
                <p style={{ color: '#FF4842', fontSize: '0.75rem', marginTop: '10px' }}>
                  {errors.icon?.message}
                </p>
              </div>
            </Stack>

            <RHFTextField fullWidth name="title" label="English Path Title" />
            <RHFTextField fullWidth name="title_ar" label="Arabic Path Title" />

            <RHFTextField
              multiline
              minRows={2}
              name="description"
              maxRows={3}
              label="English Description"
            />
            <RHFTextField
              multiline
              minRows={2}
              name="description_ar"
              maxRows={3}
              label="Arabic Description"
            />

            <Autocomplete
              multiple
              id="keywords"
              options={[]}
              value={values.keywords}
              freeSolo
              renderTags={(value: string[], getTagProps) =>
                value.map((option: string, index: number) => (
                  <Chip
                    key={index}
                    variant="outlined"
                    label={option}
                    onDelete={(e) => {
                      values.keywords.splice(index, 1);

                      setValue('keywords', values.keywords);
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <>
                  <TextField
                    {...params}
                    error={errors.keywords && values.keywords.length < 1 ? true : false}
                    name={'keywords'}
                    onKeyDown={(e) => {
                      let event = e as any;
                      if (e.key === 'Enter') {
                        setValue('keywords', [...values.keywords, event?.target?.value as any]);
                      }
                      if (e.key == 'Backspace') {
                        const value = values?.keywords;
                        value?.pop();

                        setValue('keywords', value);
                      }
                    }}
                    variant="outlined"
                    label="English Keywords"
                    placeholder="Keywords"
                    maxRows={3}
                  />
                  <span style={{ marginBottom: '10px', color: '#FF4842', fontSize: '0.75rem' }}>
                    {errors.keywords?.message &&
                      values.keywords.length < 1 &&
                      errors.keywords?.message}
                  </span>
                </>
              )}
            />

            <Autocomplete
              multiple
              id="keywords_ar"
              options={[]}
              value={values?.keywords_ar ?? []}
              freeSolo
              renderTags={(value: string[], getTagProps) =>
                value.map((option: string, index: number) => (
                  <Chip
                    key={index}
                    variant="outlined"
                    label={option}
                    onDelete={(e) => {
                      values?.keywords_ar?.splice(index, 1);

                      setValue('keywords_ar', values?.keywords_ar);
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <>
                  <TextField
                    {...params}
                    name={'keywords_ar'}
                    onKeyDown={(e) => {
                      let event = e as any;
                      if (e.key === 'Enter') {
                        setValue(
                          'keywords_ar',
                          values?.keywords_ar
                            ? [...values?.keywords_ar, event?.target?.value as any]
                            : [event?.target?.value as any]
                        );
                      }
                      if (e.key == 'Backspace') {
                        const value = values?.keywords_ar;
                        value?.pop();

                        setValue('keywords_ar', value);
                      }
                    }}
                    variant="outlined"
                    placeholder="keywords"
                    label="Arabic Keywords"
                    maxRows={3}
                  />
                </>
              )}
            />
          </Stack>

          <Stack alignItems="flex-end" sx={{ mt: 3 }}>
            <LoadingButton
              style={{ width: '200px', height: '50px' }}
              type="submit"
              variant="contained"
              loading={isSubmitting}
            >
              {!isEdit ? 'Create Path' : 'Save Changes'}
            </LoadingButton>
          </Stack>
        </Stack>
      </FormProvider>
    </Stack>
  );
}
